import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      setDone(true);
      toast.success(data.message || "Message sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    } finally {
      setLoading(false);
    }
  };

  const field = "mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary";
  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <section className="mx-auto max-w-5xl px-4 py-20 sm:px-8">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Contact</p>
            <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl">Let's talk builds.</h1>
            <p className="mt-4 text-muted-foreground">
              Questions about pricing, a big project, or partnering with us? Send a note and the team will get back to you.
            </p>
            <div className="mt-8 space-y-3 font-mono2 text-sm text-muted-foreground">
              <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> hello@cost2build.ai</p>
              <p className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Typical reply within 1 business day</p>
            </div>
          </div>

          <div className="border border-border p-7">
            {done ? (
              <div className="flex min-h-[280px] flex-col items-center justify-center text-center" data-testid="contact-done">
                <CheckCircle2 className="h-12 w-12 text-primary" />
                <h3 className="mt-4 font-heading text-xl font-semibold">Message sent</h3>
                <p className="mt-1 text-muted-foreground">Thanks — we'll be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div>
                  <Label className={lbl}>Name</Label>
                  <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" className={field} />
                </div>
                <div>
                  <Label className={lbl}>Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className={field} />
                </div>
                <div>
                  <Label className={lbl}>Message</Label>
                  <Textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className={field} />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit"
                  className="w-full rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
